import { useCallback, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ToggleButtonGroup, Tooltip, Typography, Divider } from '@mui/material';
import { useLocation } from 'react-router-dom';
import useSearchParams from '../../hooks/useSearchParams';
import { ReactComponent as WorkbookIcon } from '../../assets/16x16/workbook.svg';
import { ReactComponent as DbtIcon } from '../../assets/20x20/dbt.svg';
import { ReactComponent as DocumentIcon } from '../../assets/20x20/document.svg';
import { ReactComponent as DwhIcon } from '../../assets/20x20/dwh.svg';
import { ReactComponent as OwnerIcon } from '../../assets/20x20/owners.svg';
import { ReactComponent as TagIcon } from '../../assets/20x20/tags.svg';
import { ReactComponent as DwhTreeItemIcon } from '../../assets/dwh-black.svg';
import { ReactComponent as HashIcon } from '../../assets/hash-icon.svg';
import { ReactComponent as SchemaIcon } from '../../assets/schema.svg';
import { ReactComponent as TableIcon } from '../../assets/tables.svg';
import useAccount from '../../hooks/useAccount';
import useDebounce from '../../hooks/useDebounce';
import useEnvPermission from '../../hooks/useEnvPermission';
import usePrevious from '../../hooks/usePrevious';
import useOSSTreeData from '../../hooks/treeData/useOSSTreeData';
import useSaaSTreeData from '../../hooks/treeData/useSaaSTreeData';
import type { TreeTotalsType } from '../../types';
import useTreeViewParams from '../../hooks/useTreeViewParams';
import useUserPreferences from '../../hooks/useUserPreferences';
import { Report } from '../../types';
import { isInSaaSMode } from '../../utils/common';
import { FlexColumn } from '../Flex';
import ToggleButton from '../ToggleButton';
import TreeView from '../TreeView';
import AsyncTreeNodeIncidentStatusChips from '../TreeView/AsyncTreeNodeIncidentStatusChips';
import AsyncTreeNodeTotalsChips from '../TreeView/AsyncTreeNodeTotalsChips';
import { TreeViewNode } from '../TreeView/TreeView';
import {
  DAYS_BACK,
  TREE_NODE,
  TREE_TYPE,
  VIEW_ID,
} from '../../constants/urlParams';
import { DEFAULT_DAYS_BACK } from '../../constants/constants';
import useSavedViews from '../../hooks/useSavedViews';
import TreeViewByMenu from './TreeViewByMenu';
import useExpandedTreeNodes from './useExpandedTreeNodes';
import SavedViews from './SavedViews';

const BOTTOM_TREE_FOLDERS = ['No tags', 'No owners'];

const ToggleButtonLabel = styled(Typography)`
  pointer-events: auto;
  font-size: inherit;
  color: inherit;
`;

const Wrapper = styled(FlexColumn)`
  padding: 24px;
  padding-top: 0;
  height: 100%;
  overflow: auto;
`;

const Container = styled(FlexColumn)`
  height: 100%;
  overflow: hidden;
  padding-top: 24px;
  gap: 16px;
`;

const EmptyStateMessage = styled(Typography)`
  align-self: center;
  color: ${({ theme }) => theme.palette.common.gray4};
  text-align: center;
  white-space: pre-line;
`;

type Props = {
  reportData?: Report;
};

const ReportTreeView = ({ reportData }: Props) => {
  const [searchValue, setSearchValue] = useState<string>('');

  const searchValueDebounced = useDebounce(searchValue);
  const { userPreferences, updateUserPreferences, removeUserPreferences } =
    useUserPreferences();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const hasPermission = useEnvPermission();
  const { data: account } = useAccount();
  const isIncidentsV2Enabled = Boolean(account?.incidents_v2_enabled);
  const { savedView } = useSavedViews({
    savedViewId: searchParams.get(VIEW_ID) || undefined,
  });

  const selectedTreeNode = useMemo(() => {
    const param = searchParams.get(TREE_NODE);
    return param ? JSON.parse(param) : null;
  }, [searchParams]);

  const {
    treeType,
    treeViewBy,
    defaultTreeViewBy,
    setTreeViewBy: setTreeViewByParam,
    deleteTreeType: deleteTreeTypeParam,
    deleteTreeViewBy: deleteTreeViewByParam,
  } = useTreeViewParams();

  const setTreeType = (type: 'data_assets' | 'bi_assets') => {
    const newParams = new URLSearchParams(searchParams);
    if (treeType !== type) {
      newParams.set(TREE_TYPE, type);
      newParams.delete(TREE_NODE);
      setSearchParams(newParams);
    }
  };

  const formattedSearchValue = useMemo(
    () =>
      treeViewBy === 'tags'
        ? searchValueDebounced?.replace('#', '')
        : searchValueDebounced,
    [searchValueDebounced, treeViewBy],
  );

  const isAsyncTreeTotalsEnabled = useMemo(
    () => Boolean(isInSaaSMode() && pathname.includes('/test-results')),
    [pathname],
  );

  const isAsyncIncidentTotalsEnabled = useMemo(
    () =>
      Boolean(
        isInSaaSMode() &&
          pathname.includes('/incidents') &&
          isIncidentsV2Enabled,
      ),
    [pathname, isIncidentsV2Enabled],
  );

  const isAsyncTotalsEnabled =
    isAsyncTreeTotalsEnabled || isAsyncIncidentTotalsEnabled;

  const asyncTotalsDaysBack = useMemo(
    () =>
      isInSaaSMode()
        ? DEFAULT_DAYS_BACK
        : reportData?.days_back ?? DEFAULT_DAYS_BACK,
    [reportData?.days_back],
  );

  const asyncIncidentTotalsDaysBack = useMemo(() => {
    const daysBackParam = searchParams.get(DAYS_BACK);
    const parsed = daysBackParam ? Number.parseInt(daysBackParam, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 3;
  }, [searchParams]);

  const savedViewFilters = useMemo(
    () => savedView?.data?.filters ?? [],
    [savedView?.data?.filters],
  );

  const renderAsyncTotals = useCallback(
    ({
      id,
      kind,
      requestKey,
    }: {
      id: string;
      kind: TreeTotalsType;
      requestKey: string;
    }) => (
      <AsyncTreeNodeTotalsChips
        key={`${kind}:${requestKey}:${id}`}
        category={treeType}
        view={treeViewBy ?? 'paths'}
        daysBack={asyncTotalsDaysBack}
        filters={savedViewFilters}
        kind={kind}
        requestKey={requestKey}
      />
    ),
    [asyncTotalsDaysBack, savedViewFilters, treeType, treeViewBy],
  );

  const renderAsyncIncidentTotals = useCallback(
    ({
      id,
      kind,
      requestKey,
    }: {
      id: string;
      kind: TreeTotalsType;
      requestKey: string;
    }) => (
      <AsyncTreeNodeIncidentStatusChips
        key={`${kind}:${requestKey}:${id}`}
        category={treeType}
        view={treeViewBy ?? 'paths'}
        daysBack={asyncIncidentTotalsDaysBack}
        filters={savedViewFilters}
        kind={kind}
        requestKey={requestKey}
      />
    ),
    [asyncIncidentTotalsDaysBack, savedViewFilters, treeType, treeViewBy],
  );

  const renderAsyncTreeTotals = isAsyncIncidentTotalsEnabled
    ? renderAsyncIncidentTotals
    : isAsyncTreeTotalsEnabled
    ? renderAsyncTotals
    : undefined;

  const showTotals = useMemo(() => {
    if (pathname.includes('/catalog')) {
      return false;
    }
    if (pathname.includes('/incidents')) {
      return hasPermission('VIEW_INCIDENTS');
    }
    return hasPermission('VIEW_TESTS');
  }, [pathname, hasPermission]);

  const useTreeData = isInSaaSMode() ? useSaaSTreeData : useOSSTreeData;

  const {
    treeViews,
    folderNodeIds,
    treeViewsLeafIds,
    isTreeDataLoading,
    hasBiData,
  } = useTreeData({
    reportData,
    searchValue: formattedSearchValue,
    treeType,
    treeViewBy,
    isAsyncTotalsEnabled,
    selectedSavedView: savedView?.data,
  });

  const topLevelFolders = useMemo(
    () =>
      treeViews?.filter((root) => !!root.children).map((root) => root.id) || [],
    [treeViews],
  );

  const { expandedNodes, setExpandedNodes } = useExpandedTreeNodes(
    treeViewBy,
    topLevelFolders,
    formattedSearchValue,
    folderNodeIds,
    selectedTreeNode,
  );

  useEffect(() => {
    if (selectedTreeNode?.id && !isTreeDataLoading) {
      if (
        !treeViewsLeafIds.includes(selectedTreeNode.id) &&
        !folderNodeIds.includes(selectedTreeNode.id)
      ) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete(TREE_NODE);
        setSearchParams(newParams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTreeNode,
    treeViewsLeafIds,
    folderNodeIds,
    topLevelFolders,
    isTreeDataLoading,
  ]);

  const handleNodeClick = useCallback(
    (node: { id: string; parentId: string }) => {
      const newParams = new URLSearchParams(searchParams);

      if (
        node.id === selectedTreeNode?.id &&
        node.parentId === selectedTreeNode?.parentId
      ) {
        newParams.delete(TREE_NODE);
        setSearchParams(newParams);
        return;
      }

      newParams.set(TREE_NODE, JSON.stringify(node));
      setSearchParams(newParams);
    },
    [searchParams, selectedTreeNode, setSearchParams],
  );

  const orderedTreeViews = useMemo(() => {
    if (!treeViews) return undefined;

    const topViews = treeViews.filter(
      (v) => !BOTTOM_TREE_FOLDERS.includes(v.name),
    );
    const bottomViews = treeViews.filter((v) =>
      BOTTOM_TREE_FOLDERS.includes(v.name),
    );

    return [
      ...topViews.sort((a, b) => (a.name > b.name ? 1 : -1)),
      ...bottomViews,
    ].filter((view) => !!view.children?.length);
  }, [treeViews]);

  const findNodeParentIdInTree = useCallback(
    (
      nodeId: string,
      treeData: TreeViewNode[] | undefined,
    ): string | undefined => {
      if (!treeData) return undefined;

      const searchInTree = (nodes: TreeViewNode[]): string | undefined => {
        for (const node of nodes) {
          if (node.id === nodeId) {
            return node.parentId;
          }
          if (node.children) {
            const found = searchInTree(node.children);
            if (found !== undefined) return found;
          }
        }
        return undefined;
      };

      return searchInTree(treeData);
    },
    [],
  );

  // update URL on tree view change - find the node's actual parentId in the new tree structure
  const previousTreeViewBy = usePrevious(treeViewBy);
  useEffect(() => {
    if (previousTreeViewBy === treeViewBy) {
      return;
    }

    if (selectedTreeNode?.id && orderedTreeViews) {
      const actualParentId = findNodeParentIdInTree(
        selectedTreeNode.id,
        orderedTreeViews,
      );

      if (actualParentId !== undefined) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set(
          TREE_NODE,
          JSON.stringify({
            ...selectedTreeNode,
            parentId: actualParentId,
          }),
        );
        setSearchParams(newParams);
      }
    }
  }, [
    treeViewBy,
    previousTreeViewBy,
    orderedTreeViews,
    selectedTreeNode?.id,
    selectedTreeNode?.columnId,
    findNodeParentIdInTree,
    setSearchParams,
    searchParams,
    selectedTreeNode,
  ]);

  const shouldShowFolderIcon = useMemo(
    () => treeViewBy !== 'owners',
    [treeViewBy],
  );

  const renderFolderIcon = useCallback(
    (data: TreeViewNode) => {
      if (treeViewBy === 'dwh') {
        return data.nodeLevel === 0 ? (
          <DwhTreeItemIcon width={10} />
        ) : (
          <SchemaIcon width={16} />
        );
      }

      if (treeViewBy === 'tags') {
        return <HashIcon />;
      }

      // todo: the tree item node type should be provided by the BE, this logic is not ideal
      if (treeType === 'bi_assets' && treeViewBy === 'paths') {
        if (data.id.toLowerCase().includes('foldernode_sigma')) {
          const isLastLevelFolder = data.children?.every((c) => !c.children);
          if (isLastLevelFolder) {
            return <WorkbookIcon />;
          }
        }
      }

      return undefined;
    },
    [treeType, treeViewBy],
  );

  const renderNodeIcon = useMemo(() => {
    if (treeViewBy === 'dwh') {
      return () => <TableIcon width={14} />;
    }

    return undefined;
  }, [treeViewBy]);

  const isBiDisabled = useMemo(() => {
    if (pathname.includes('/lineage') || pathname.includes('/catalog')) {
      return !hasBiData || pathname.includes('/coverage');
    }
    return true;
  }, [pathname, hasBiData]);

  const biButtonTooltip = useMemo(() => {
    if (
      pathname.includes('/test-results') ||
      pathname.includes('/test-runs') ||
      pathname.includes('/test-management')
    ) {
      return 'Tests are not supported for BI assets';
    }
    if (pathname.includes('/metrics')) {
      return 'Metrics are not supported for BI assets';
    }
    if (pathname.includes('/model-runs')) {
      return 'Model performance is not supported for BI assets';
    }
    if (!hasBiData) {
      return isInSaaSMode()
        ? 'No BI tool connected to this environment. Please connect your BI tool to view assets'
        : 'BI assets are supported only in Elementary cloud';
    }
    return undefined;
  }, [pathname, hasBiData]);

  const treeTypeTabs = useMemo(
    () => [
      { value: 'data_assets', label: 'Data Assets' },
      {
        value: 'bi_assets',
        label: 'BI Assets',
        disabled: isBiDisabled,
        tooltip: isBiDisabled ? biButtonTooltip : undefined,
      },
    ],
    [biButtonTooltip, isBiDisabled],
  );

  const viewByOptions = useMemo(
    () =>
      treeType === 'data_assets'
        ? [
            {
              label: 'DWH',
              value: 'dwh',
              icon: <DwhIcon />,
            },
            {
              label: 'Tags',
              value: 'tags',
              icon: <TagIcon />,
            },
            {
              label: 'Owners',
              value: 'owners',
              icon: <OwnerIcon />,
            },
            {
              label: 'dbt',
              value: 'dbt',
              icon: <DbtIcon />,
            },
          ]
        : [
            {
              label: 'Paths',
              value: 'paths',
              icon: <DocumentIcon />,
            },
            {
              label: 'Tags',
              value: 'tags',
              icon: <TagIcon />,
            },
            {
              label: 'Owners',
              value: 'owners',
              icon: <OwnerIcon />,
            },
          ],
    [treeType],
  );

  const handleViewByChange = (val: string) => {
    if (val === 'paths' || val === 'dbt') {
      deleteTreeViewByParam();
    } else {
      setTreeViewByParam(val);
    }
  };

  const isLoading = useMemo(
    () => !orderedTreeViews || isTreeDataLoading,
    [isTreeDataLoading, orderedTreeViews],
  );

  const isEmpty = useMemo(
    () => orderedTreeViews && orderedTreeViews?.length === 0,
    [orderedTreeViews],
  );

  useEffect(() => {
    if (!treeViewBy || treeViewBy === defaultTreeViewBy) {
      removeUserPreferences('treeViewBy');
    } else if (
      treeViewBy &&
      !viewByOptions.some((o) => o.value === treeViewBy)
    ) {
      deleteTreeViewByParam();
      removeUserPreferences('treeViewBy');
    } else if (treeViewBy) {
      updateUserPreferences('treeViewBy', treeViewBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeViewBy, searchParams, treeType]);

  useEffect(() => {
    if (userPreferences.treeViewBy && !treeViewBy) {
      handleViewByChange(userPreferences.treeViewBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const selectedTab = treeTypeTabs.find((t) => t.value === treeType);
    if (selectedTab?.disabled) {
      deleteTreeTypeParam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeTypeTabs, treeType]);

  return (
    <Container>
      <FlexColumn sx={{ minWidth: '174px', gap: 2 }}>
        <FlexColumn sx={{ px: 3 }}>
          <ToggleButtonGroup
            value={treeType}
            onChange={(_, val) => setTreeType(val || 'data_assets')}
            exclusive
            fullWidth
          >
            {treeTypeTabs.map((tab) => (
              <ToggleButton
                key={tab.value}
                role="button"
                value={tab.value}
                disabled={tab.disabled}
              >
                <Tooltip title={tab.tooltip}>
                  <ToggleButtonLabel>{tab.label}</ToggleButtonLabel>
                </Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </FlexColumn>

        <FlexColumn sx={{ gap: 1 }}>
          {treeType === 'data_assets' && isInSaaSMode() && (
            <>
              <Divider />
              <FlexColumn sx={{ px: 3 }}>
                <SavedViews />
              </FlexColumn>
            </>
          )}

          <Divider />
        </FlexColumn>

        <FlexColumn sx={{ px: 3 }}>
          <TreeViewByMenu
            search={searchValue}
            onSearch={setSearchValue}
            treeViewBy={treeViewBy}
            viewByOptions={viewByOptions}
            onViewByChange={handleViewByChange}
            disabled={isLoading}
          />
        </FlexColumn>
      </FlexColumn>

      <Wrapper>
        {isLoading ? (
          <TreeView isLoading />
        ) : isEmpty ? (
          <FlexColumn sx={{ alignItems: 'center', gap: 1, mt: 2 }}>
            <EmptyStateMessage fontWeight="bold">No data</EmptyStateMessage>
            <EmptyStateMessage>
              {`No matching data to display.\nCheck your filters/search/view selection.`}
            </EmptyStateMessage>
          </FlexColumn>
        ) : (
          orderedTreeViews?.map((treeData) => (
            <TreeView
              key={treeData.id}
              data={treeData}
              onNodeClick={handleNodeClick}
              selected={selectedTreeNode}
              searchQuery={formattedSearchValue}
              onNodeExpandCollapse={setExpandedNodes}
              expanded={expandedNodes}
              showFolderIcon={shouldShowFolderIcon}
              renderFolderIcon={renderFolderIcon}
              renderNodeIcon={renderNodeIcon}
              showTotals={showTotals}
              renderTotals={renderAsyncTreeTotals}
            />
          ))
        )}
      </Wrapper>
    </Container>
  );
};

export default ReportTreeView;
